/**
 * Guided Filter — He, Sun, Tang (2010), "Guided Image Filtering."
 *
 * Single-channel guided filter used by Mertens exposure-fusion to smooth
 * per-bracket weight maps along the structure of a guide image (Rec.709
 * luminance of a reference bracket). Kills pyramid ringing without
 * blurring across real edges.
 *
 * O(N) per call regardless of filter radius — box filter via separable
 * 1D moving-average. Same separable formulation is used on the GPU,
 * keeping CPU↔GPU bit-parity feasible (≤4e-3 in Float32).
 *
 * Boundary handling: window clamps to image bounds; the divisor is the
 * actual sample count (NOT zero-padded). This avoids artificially
 * darkening the filtered edges, which would distort the guided-filter
 * a/b coefficients.
 */

/**
 * Apply a guided filter to a single-channel input using a single-channel
 * guide. Both arrays must be Float32 of length width*height, row-major.
 *
 * @param {Float32Array} input   width*height single-channel (the "p" map)
 * @param {Float32Array} guide   width*height single-channel guide ("I")
 * @param {number} width
 * @param {number} height
 * @param {number} radius        box radius in pixels (window = 2r+1)
 * @param {number} eps           regularisation, typical 1e-3
 * @returns {Float32Array}       filtered output (new buffer)
 */
export function guidedFilter(input, guide, width, height, radius, eps) {
    const N = width * height;
    if (input.length !== N || guide.length !== N) {
        throw new Error('guidedFilter: input/guide length mismatch');
    }
    if (!Number.isFinite(radius) || radius < 1) {
        throw new Error('guidedFilter: radius must be ≥ 1');
    }
    if (!Number.isFinite(eps) || eps <= 0) {
        throw new Error('guidedFilter: eps must be > 0');
    }
    const r = Math.max(1, Math.round(radius));

    /* Standard He-Sun-Tang formulation. The earlier "centered" variant
     * (`boxFilter((I − meanI[i])·(p − meanP[i]))`) was caught by Codex
     * R5 as algebraically wrong: each sample gets centered against its
     * OWN local mean field before averaging, which is not the same as
     * the per-window cov(I, p). Reverting to the textbook moments. */
    const Ip = new Float32Array(N);
    const II = new Float32Array(N);
    for (let i = 0; i < N; i++) {
        const gi = guide[i];
        const pi = input[i];
        Ip[i] = gi * pi;
        II[i] = gi * gi;
    }

    const meanI  = boxFilter(guide, width, height, r);
    const meanP  = boxFilter(input, width, height, r);
    const meanIp = boxFilter(Ip,    width, height, r);
    const meanII = boxFilter(II,    width, height, r);

    /* Linear coefficients a, b per pixel (He-Sun-Tang Eqs. 5 & 6). */
    const a = new Float32Array(N);
    const b = new Float32Array(N);
    for (let i = 0; i < N; i++) {
        const cov  = meanIp[i] - meanI[i] * meanP[i];
        const varI = meanII[i] - meanI[i] * meanI[i];
        const ai = cov / (varI + eps);
        a[i] = ai;
        b[i] = meanP[i] - ai * meanI[i];
    }

    /* Smooth a, b across the same window (averages overlapping windows
     * → spatially varying linear model). He-Sun-Tang Eq. (7). */
    const meanA = boxFilter(a, width, height, r);
    const meanB = boxFilter(b, width, height, r);

    /* Reconstruct: q = mean(a) ⋅ I + mean(b). */
    const q = new Float32Array(N);
    for (let i = 0; i < N; i++) {
        q[i] = meanA[i] * guide[i] + meanB[i];
    }
    return q;
}

/**
 * Separable box filter via 1D moving-average. O(N) regardless of radius.
 * Boundary: window clamps to bounds, divisor is the actual sample count.
 *
 * @param {Float32Array} src     width*height single-channel
 * @param {number} width
 * @param {number} height
 * @param {number} r             box radius (window = 2r+1)
 * @returns {Float32Array}       filtered output (new buffer)
 */
export function boxFilter(src, width, height, r) {
    const N = width * height;
    const tmp = new Float32Array(N);

    /* Horizontal pass — moving-average along x. */
    for (let y = 0; y < height; y++) {
        const rowOff = y * width;
        let sum = 0;
        const initEnd = Math.min(r, width - 1);
        for (let x = 0; x <= initEnd; x++) sum += src[rowOff + x];
        let count = initEnd + 1;
        tmp[rowOff] = sum / count;
        for (let x = 1; x < width; x++) {
            const remIdx = x - r - 1;
            if (remIdx >= 0) {
                sum -= src[rowOff + remIdx];
                count -= 1;
            }
            const addIdx = x + r;
            if (addIdx < width) {
                sum += src[rowOff + addIdx];
                count += 1;
            }
            tmp[rowOff + x] = sum / count;
        }
    }

    /* Vertical pass — moving-average along y over the H-pass result. */
    const out = new Float32Array(N);
    for (let x = 0; x < width; x++) {
        let sum = 0;
        const initEnd = Math.min(r, height - 1);
        for (let y = 0; y <= initEnd; y++) sum += tmp[y * width + x];
        let count = initEnd + 1;
        out[x] = sum / count;
        for (let y = 1; y < height; y++) {
            const remIdx = y - r - 1;
            if (remIdx >= 0) {
                sum -= tmp[remIdx * width + x];
                count -= 1;
            }
            const addIdx = y + r;
            if (addIdx < height) {
                sum += tmp[addIdx * width + x];
                count += 1;
            }
            out[y * width + x] = sum / count;
        }
    }

    return out;
}

/**
 * Compute Rec.709 luminance map from an RGBA Float32 buffer.
 * Used to build the guide image from the chosen reference bracket.
 *
 * @param {Float32Array} rgba    width*height*4 RGBA Float32
 * @param {number} width
 * @param {number} height
 * @returns {Float32Array}       width*height single-channel luminance
 */
export function luminanceFromRGBA(rgba, width, height) {
    const N = width * height;
    if (rgba.length !== N * 4) {
        throw new Error('luminanceFromRGBA: rgba length must be width*height*4');
    }
    const out = new Float32Array(N);
    for (let i = 0, p = 0; i < N; i++, p += 4) {
        out[i] = 0.2126 * rgba[p] + 0.7152 * rgba[p + 1] + 0.0722 * rgba[p + 2];
    }
    return out;
}

/**
 * Pick the guide bracket index from a stack.
 *
 * Selection rule (Codex R1 spec):
 *   1. If `evStops` is provided and all entries are finite, pick the
 *      bracket at the median EV. (Stable middle index after sort.)
 *   2. Else: pick the bracket whose mean log-luminance over the image
 *      is closest to `muEV` (= log2(0.18) by default). Mean is taken
 *      over all pixels — small extra cost, gives the most stable edge
 *      structure even when extremes are blown/clipped.
 *
 * @param {Array<{ rgba: Float32Array, width: number, height: number }>} stack
 * @param {Float32Array | number[] | null} evStops
 * @param {number} muEV
 * @param {number} evFloor
 * @returns {number}             chosen bracket index
 */
export function selectGuideIndex(stack, evStops, muEV, evFloor) {
    if (!Array.isArray(stack) || stack.length === 0) {
        throw new Error('selectGuideIndex: empty stack');
    }
    const N = stack.length;
    const evOk = evStops && evStops.length === N
        && Array.from(evStops).every(Number.isFinite);
    if (evOk) {
        const order = new Array(N);
        for (let i = 0; i < N; i++) order[i] = { i, ev: evStops[i] };
        order.sort((a, b) => a.ev - b.ev);
        return order[Math.floor((N - 1) / 2)].i;
    }

    const floor = Number.isFinite(evFloor) ? evFloor : 1e-6;
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let k = 0; k < N; k++) {
        const { rgba, width, height } = stack[k];
        const px = width * height;
        let sum = 0;
        for (let i = 0, p = 0; i < px; i++, p += 4) {
            const L = 0.2126 * rgba[p] + 0.7152 * rgba[p + 1] + 0.0722 * rgba[p + 2];
            sum += Math.log2(Math.max(L, floor));
        }
        const meanLogL = sum / px;
        const dist = Math.abs(meanLogL - muEV);
        if (dist < bestDist) {
            bestDist = dist;
            bestIdx = k;
        }
    }
    return bestIdx;
}

/* Codex R1 lock-in: radius range. The GPU shader (FRAG_BOX_H_R / V_R)
 * hard-caps the loop at MAX_R = 32, so any caller-supplied radius outside
 * [MIN_RADIUS, MAX_RADIUS] would silently diverge CPU vs GPU. Both paths
 * route through clampRadius() at their fuseBrackets entries. */
export const MIN_RADIUS = 4;
export const MAX_RADIUS = 32;

/**
 * Clamp a caller-supplied (or computed) radius to the locked range.
 * Returns the integer in [MIN_RADIUS, MAX_RADIUS] used by both CPU
 * guidedFilter() and the GPU box-filter shaders.
 */
export function clampRadius(r) {
    if (!Number.isFinite(r)) return MIN_RADIUS;
    const ri = Math.round(r);
    if (ri < MIN_RADIUS) return MIN_RADIUS;
    if (ri > MAX_RADIUS) return MAX_RADIUS;
    return ri;
}

/**
 * Default guided-filter radius given image dimensions.
 * Codex R1: ~1.75% of short edge, clamped to [MIN_RADIUS, MAX_RADIUS].
 *
 * @param {number} width
 * @param {number} height
 * @returns {number}             radius in pixels
 */
export function defaultRadius(width, height) {
    const shortEdge = Math.min(width, height);
    return clampRadius(shortEdge * 0.0175);
}

/** Default regularisation epsilon for Float32 weight maps in [0, 1]. */
export const DEFAULT_GUIDED_EPS = 1e-3;
